/* ============================================================
   24GAMEKEYS — Digital delivery UI helpers   v3.1
   ------------------------------------------------------------
   Client-side display utilities only.
   No network calls. No credentials. No third-party references.
   All supplier communication happens server-side.

   Security notes:
   - Values are inserted with textContent, never interpolated
     into innerHTML, so a hostile product name or key string
     cannot execute script on the page.
   - No internal configuration is exposed on window.
   - Backward compatible with older markup that calls
     GK.copyKey('<id>', this) from an inline handler.
   ============================================================ */

(function () {
  'use strict';

  var LOW_STOCK_THRESHOLD = 5;
  var COPY_FEEDBACK_MS = 2000;

  /* ── STOCK BADGE ─────────────────────────────────────────── */

  function badgeState(count) {
    if (count === 0) return 'outofstock';
    if (count <= LOW_STOCK_THRESHOLD) return 'lowstock';
    return 'instock';
  }

  function badgeLabel(count) {
    if (count === 0) return 'Out of stock';
    if (count <= LOW_STOCK_THRESHOLD) return 'Low stock (' + count + ')';
    return 'In stock (' + count + ')';
  }

  /**
   * Build a stock badge as a DOM node.
   * @param {number} count
   * @returns {HTMLElement|null}
   */
  function buildStockBadge(count) {
    var n = Number(count);
    if (count === null || count === undefined || isNaN(n)) return null;

    var state = badgeState(n);
    var badge = document.createElement('span');
    badge.className = 'gk-stock-badge gk-stock-badge--' + state;

    var dot = document.createElement('span');
    dot.className = 'gk-stock-dot gk-stock-dot--' + state;

    badge.appendChild(dot);
    badge.appendChild(document.createTextNode(badgeLabel(n)));
    return badge;
  }

  /**
   * Render a stock badge.
   * Two call styles are supported:
   *   renderStockBadge(count)            -> returns an HTML string (legacy)
   *   renderStockBadge(element, count)   -> renders into the element
   * @returns {string|undefined}
   */
  function renderStockBadge(a, b) {
    // Legacy: a single numeric argument, caller expects a string back.
    if (arguments.length < 2 || a === null || a === undefined || typeof a === 'number' || typeof a === 'string') {
      var legacyBadge = buildStockBadge(arguments.length < 2 ? a : b);
      return legacyBadge ? legacyBadge.outerHTML : '';
    }
    // Preferred: (container, count)
    var container = a;
    if (!container || !container.appendChild) return '';
    var badge = buildStockBadge(b);
    container.textContent = '';
    if (badge) container.appendChild(badge);
    return undefined;
  }

  /* ── COPY KEY ────────────────────────────────────────────── */

  /**
   * Resolve whatever the caller passed into the element holding the key.
   * Accepts an element, an element id, or a bare id used with the
   * historical "key-<id>" naming convention.
   */
  function resolveKeyElement(ref) {
    if (!ref) return null;
    if (typeof ref === 'string') {
      return document.getElementById(ref) || document.getElementById('key-' + ref);
    }
    if (ref.nodeType === 1) return ref;
    return null;
  }

  function selectFallback(el) {
    try {
      var range = document.createRange();
      range.selectNodeContents(el);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      /* nothing further we can do */
    }
  }

  /**
   * Copy a delivered key to the clipboard.
   * @param {HTMLElement|string} keyRef element or element id
   * @param {HTMLElement} [btn] button that triggered the copy
   */
  function copyKey(keyRef, btn) {
    var keyEl = resolveKeyElement(keyRef);
    if (!keyEl) return;

    var text = (keyEl.textContent || '').trim();
    if (!text) return;

    function feedback() {
      if (!btn) return;
      // Preserve any icon markup inside the button: capture, replace the
      // visible text safely, then restore the original trusted markup.
      var originalHTML = btn.innerHTML;
      if (btn.dataset) btn.dataset.gkBusy = '1';
      btn.textContent = '\u2713 Copied';
      btn.classList.add('is-copied');
      setTimeout(function () {
        btn.innerHTML = originalHTML;
        btn.classList.remove('is-copied');
        if (btn.dataset) delete btn.dataset.gkBusy;
      }, COPY_FEEDBACK_MS);
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(feedback).catch(function () {
        selectFallback(keyEl);
      });
    } else {
      selectFallback(keyEl);
    }
  }

  /* ── EVENT WIRING ────────────────────────────────────────── */

  /**
   * Delegated handler for the modern markup:
   *   <div class="gk-delivery-box">
   *     <code data-gk-key>XXXX-XXXX</code>
   *     <button type="button" data-gk-copy>Copy key</button>
   *   </div>
   */
  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var btn = target.closest('[data-gk-copy]');
    if (!btn) return;
    if (btn.dataset && btn.dataset.gkBusy === '1') return;

    event.preventDefault();

    var explicit = btn.getAttribute('data-gk-copy');
    var keyEl = explicit ? resolveKeyElement(explicit) : null;

    if (!keyEl) {
      var box = btn.closest('.gk-delivery-box') || btn.parentElement;
      keyEl = box ? box.querySelector('[data-gk-key]') : null;
    }

    copyKey(keyEl, btn);
  }

  function init() {
    if (window.__gkDeliveryReady) return;
    window.__gkDeliveryReady = true;
    document.addEventListener('click', onDocumentClick);
  }

  /* ── PUBLIC API ──────────────────────────────────────────── */
  window.GK = window.GK || {};
  window.GK.copyKey = copyKey;
  window.GK.renderStockBadge = renderStockBadge;

  /* ── INIT ────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();